import Link from 'next/link';

import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { CellValue, CheckedNote, OurMark } from './kit';
import type { AlternativePageProps } from './types';
import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  delay,
  Stage,
  tone,
  useOpenAgentDemo,
} from '../primitives';

type Props = Pick<
  AlternativePageProps,
  'name' | 'keyword' | 'hero' | 'glance' | 'category'
>;

export default function AlternativeHero({
  name,
  keyword,
  hero,
  glance,
  category,
}: Props) {
  const openDemo = useOpenAgentDemo();

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto grid max-w-7xl gap-x-12 gap-y-14 px-6 pb-4 pt-10 sm:pt-14 lg:grid-cols-12 lg:items-center lg:px-8'>
        <div className='min-w-0 lg:col-span-6'>
          <Breadcrumb name={name} />

          {/* The H1 carries the phrase people search for; the big line
              below is the headline people read. */}
          <h1
            className={clsxm(
              styles.enter,
              'mt-8 flex items-center gap-3 text-[0.9375rem] font-semibold leading-6 text-primary-700 dark:text-primary-300',
            )}
            style={delay(30)}
          >
            <span
              aria-hidden='true'
              className='h-px w-8 flex-none bg-current opacity-50'
            />
            {keyword}
          </h1>
          <p
            className={clsxm(
              styles.display,
              styles.enter,
              'mt-5 text-[2.5rem] font-semibold leading-[1.04] sm:text-[3.25rem] sm:leading-[1.02] xl:text-[3.75rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            {hero.title}{' '}
            <span className='text-primary-600 dark:text-primary-400'>
              {hero.accent}
            </span>
          </p>

          <p
            className={clsxm(
              styles.enter,
              'mt-6 max-w-xl text-lg leading-8 sm:text-xl sm:leading-[1.6]',
              tone.body,
            )}
            style={delay(140)}
          >
            {hero.lead}
          </p>

          <div
            className={clsxm(styles.enter, 'mt-8 flex flex-wrap gap-3')}
            style={delay(200)}
          >
            <Link href='/login?preset=bots' className={buttonPrimary}>
              Build your agent free
              <ArrowGlyph />
            </Link>
            <button
              type='button'
              onClick={openDemo}
              className={buttonSecondary}
            >
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none' />
                <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
              </span>
              Talk to our agent
            </button>
          </div>
          <p
            className={clsxm(
              styles.enter,
              'mt-6 flex flex-wrap items-center gap-x-4 gap-y-2',
            )}
            style={delay(240)}
          >
            <CheckedNote />
            <a
              href='#sources'
              className={clsxm(
                'text-sm underline decoration-[#0f0f28]/20 underline-offset-4 transition-colors hover:text-primary-700 dark:decoration-white/20 dark:hover:text-primary-300',
                tone.quiet,
              )}
            >
              Every {name} fact links to its source
            </a>
          </p>
        </div>

        <div className='-mx-3 min-w-0 sm:mx-0 lg:col-span-6'>
          <Stage
            variant='indigo'
            halftoneAt='100% 0%'
            className={clsxm(
              styles.enter,
              'px-3 py-8 sm:px-10 sm:py-12 lg:px-8 xl:px-10',
            )}
            style={delay(280)}
          >
            <GlanceCard name={name} category={category} glance={glance} />
          </Stage>
        </div>
      </div>
    </header>
  );
}

function Breadcrumb({ name }: { name: string }) {
  return (
    <nav aria-label='Breadcrumb' className={styles.enter}>
      <ol
        className={clsxm(
          'inline-flex max-w-full items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
          tone.quiet,
        )}
      >
        <li>
          <Link
            href='/chatbots'
            className='transition-colors hover:text-primary-700 dark:hover:text-primary-300'
          >
            AI Agents
          </Link>
        </li>
        <li aria-hidden='true' className='opacity-50'>
          /
        </li>
        <li>
          <Link
            href='/alternatives'
            className='transition-colors hover:text-primary-700 dark:hover:text-primary-300'
          >
            Comparisons
          </Link>
        </li>
        <li aria-hidden='true' className='opacity-50'>
          /
        </li>
        <li aria-current='page' className={clsxm('truncate', tone.ink)}>
          {name}
        </li>
      </ol>
    </nav>
  );
}

/** The comparison in five rows, on the hero's stage. */
function GlanceCard({
  name,
  category,
  glance,
}: Pick<Props, 'name' | 'category' | 'glance'>) {
  return (
    <div
      className={clsxm(
        'relative mx-auto max-w-[34rem] overflow-hidden rounded-[22px] shadow-[0_40px_90px_-40px_rgba(20,10,80,0.75)]',
        tone.surface,
        tone.line,
      )}
    >
      <div className='grid grid-cols-2 border-b border-[#0f0f28]/[0.07] dark:border-white/[0.08]'>
        <div className='flex min-w-0 items-center gap-2.5 bg-primary-50/70 px-4 py-4 dark:bg-primary-400/[0.08] sm:px-5'>
          <OurMark className='hidden sm:block' />
          <span className='min-w-0'>
            <span
              className={clsxm(
                'block truncate text-[15px] font-semibold',
                tone.ink,
              )}
            >
              Easy-Peasy.AI
            </span>
            <span className={clsxm('block truncate text-xs', tone.quiet)}>
              AI agent builder
            </span>
          </span>
        </div>
        <div className='flex min-w-0 flex-col justify-center border-s border-[#0f0f28]/[0.07] px-4 py-4 dark:border-white/[0.08] sm:px-5'>
          <span
            className={clsxm(
              'block truncate text-[15px] font-semibold',
              tone.ink,
            )}
          >
            {name}
          </span>
          <span className={clsxm('block truncate text-xs', tone.quiet)}>
            {category}
          </span>
        </div>
      </div>

      <dl>
        {glance.map((row) => (
          <div
            key={row.key}
            className='border-b border-[#0f0f28]/[0.06] last:border-b-0 dark:border-white/[0.07]'
          >
            <dt
              className={clsxm(
                'px-4 pt-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] sm:px-5',
                tone.quiet,
              )}
            >
              {row.label}
            </dt>
            <dd className='grid grid-cols-2'>
              <span className='min-w-0 px-4 pb-3.5 pt-1.5 sm:px-5'>
                <CellValue cell={row.ours} size='sm' />
              </span>
              <span className='min-w-0 border-s border-[#0f0f28]/[0.06] px-4 pb-3.5 pt-1.5 dark:border-white/[0.07] sm:px-5'>
                <CellValue cell={row.theirs} size='sm' />
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
